import {
	useEffect,
	useMemo,
	useRef,
	useReducer,
	useCallback,
	useState,
} from "react"
import { $PropertyType, Required } from "utility-types"
import {
	TViews,
	IDateTimeState,
	IDateTimeHelpers,
	IDateTimeConfig,
	TDuration,
} from "./types"
import {
	startOf,
	parseDate,
	isSame,
	getUnit,
	formatDate,
	isBetween,
	isSameOrBefore,
	isSameOrAfter,
	addUnit,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import { alwaysValidDate, VIEWS, getDateTimeFormats } from "./utils"
import { DateTimeProvider, useDateTimeContext } from "./DateTimeContext"
import DaysView from "./DaysView"
import MonthsView from "./MonthsView"
import YearsView from "./YearsView"
import TimesView from "./TimesView"
import { datetimeClassName } from "./datetime.css"

type TDateTimeActions =
	| { type: "SET_VIEW"; payload: TViews }
	| { type: "SET_VIEW_DATE"; payload: Date }
	| { type: "SET_HOVER_DATE"; payload?: Date }
	| {
			type: "NAVIGATE_FORWARD"
			payload: { amount: number; type: TDuration }
	  }
	| {
			type: "NAVIGATE_BACK"
			payload: { amount: number; type: TDuration }
	  }

function dateTimeReducer(
	state: IDateTimeState,
	action: TDateTimeActions
): IDateTimeState {
	switch (action.type) {
		case "SET_VIEW":
			return { ...state, view: action.payload }
		case "SET_VIEW_DATE":
			return { ...state, viewDate: action.payload }
		case "SET_HOVER_DATE":
			return { ...state, hoverDate: action.payload }
		case "NAVIGATE_FORWARD":
			return {
				...state,
				viewDate: addUnit(
					state.viewDate,
					action.payload.amount,
					action.payload.type
				),
				hoverDate: undefined, // Clear hover date when navigating
			}
		case "NAVIGATE_BACK":
			return {
				...state,
				viewDate: subtractUnit(
					state.viewDate,
					action.payload.amount,
					action.payload.type
				),
				hoverDate: undefined, // Clear hover date when navigating
			}
		default:
			return state
	}
}

export function useDateTime(config: IDateTimeConfig) {
	const {
		timeFormat: configTimeFormat,
		dateFormat: configDateFormat = true,
		children,
		onChange,
		value: configValue,
		clearable = true,
		min,
		max,
		isValidDate: configIsValidDate,
		mode = "single",
		rangeValue,
		onRangeChange,
		hoverDate: externalHoverDate,
		onHoverDateChange,
		...props
	} = config

	// get the value from the props value
	// make sure we don't update if the string values is not updated
	const configValueStr = useMemo(
		() =>
			configValue instanceof Date
				? configValue.toISOString()
				: (configValue || "").toString(),
		[configValue]
	)
	const value = useMemo(() => {
		return !configValueStr ? undefined : parseDate(configValueStr)
	}, [configValueStr])
	const { timeFormat, dateFormat, dateTimeFormat } = useMemo(
		() => getDateTimeFormats(configDateFormat, configTimeFormat),
		[configTimeFormat, configDateFormat]
	)

	const initialView = dateFormat ? VIEWS["DAYS"] : VIEWS["TIME"]

	const [initialViewDate] = useState(() =>
		startOf(
			value ? parseDate(value) : min ? parseDate(min) : new Date(),
			"month"
		)
	)

	const [state, dispatch] = useReducer<
		React.Reducer<IDateTimeState, TDateTimeActions>
	>(dateTimeReducer, {
		view: initialView,
		viewDate: initialViewDate,
	})

	const setViewDate = useCallback<
		$PropertyType<IDateTimeHelpers, "setViewDate">
	>((viewDate) => {
		dispatch({ type: "SET_VIEW_DATE", payload: viewDate })
	}, [])

	const showView = useCallback<$PropertyType<IDateTimeHelpers, "showView">>(
		(view) => {
			dispatch({ type: "SET_VIEW", payload: view })
		},
		[]
	)

	const navigateForward = useCallback<
		$PropertyType<IDateTimeHelpers, "navigateForward">
	>(
		(amount, type) => {
			dispatch({ type: "NAVIGATE_FORWARD", payload: { amount, type } })
			// Clear external hoverDate when navigating
			if (onHoverDateChange) {
				onHoverDateChange(undefined)
			}
		},
		[onHoverDateChange]
	)

	const navigateBack = useCallback<
		$PropertyType<IDateTimeHelpers, "navigateBack">
	>(
		(amount, type) => {
			dispatch({ type: "NAVIGATE_BACK", payload: { amount, type } })
			// Clear external hoverDate when navigating
			if (onHoverDateChange) {
				onHoverDateChange(undefined)
			}
		},
		[onHoverDateChange]
	)

	const setHoverDate = useCallback<
		$PropertyType<IDateTimeHelpers, "setHoverDate">
	>(
		(date) => {
			if (onHoverDateChange) {
				// Use external handler if provided (for dual calendar support)
				onHoverDateChange(date)
			} else {
				// Use internal state if no external handler
				dispatch({ type: "SET_HOVER_DATE", payload: date })
			}
		},
		[onHoverDateChange]
	)

	const handleChange = useCallback(
		(date?: Date) => {
			if (!onChange) {
				return
			}
			if (clearable && value && date && !configTimeFormat) {
				if (isSame(parseDate(value), date, "day")) {
					onChange()
					return
				}
			}
			onChange(date)
		},
		[onChange, value, configTimeFormat, clearable]
	)

	// Range mode change handler
	const handleRangeChange = useCallback(
		(date?: Date) => {
			if (!onRangeChange || !date) {
				return
			}
			const { from, to } = rangeValue || {}

			// If no "from" date, or both are set, start new selection
			if (!from || (from && to)) {
				onRangeChange({ from: date, to: undefined })
				setHoverDate(undefined)
			} else {
				// Second selection: set "to" date
				if (isSameOrAfter(date, from, "day")) {
					onRangeChange({ from, to: date })
				} else {
					// User selected date before "from", flip the dates
					onRangeChange({ from: date, to: from })
				}
				setHoverDate(undefined)
			}
		},
		[onRangeChange, rangeValue, setHoverDate]
	)
	// automatic change the viewDate if current select date
	// is not in the current month
	const formattedValue = value ? formatDate(value, dateTimeFormat) : value
	const stateRef = useRef(state)
	useEffect(() => {
		stateRef.current = state
	}, [state])
	useEffect(() => {
		const { view, viewDate } = stateRef.current
		const value = formattedValue
			? parseDate(formattedValue, dateTimeFormat)
			: undefined
		if (
			view === "days" &&
			value &&
			(getUnit(viewDate, "month") !== getUnit(value, "month") ||
				getUnit(viewDate, "year") !== getUnit(value, "year"))
		) {
			// Use startOf to get the first day of the month, preserving the year from value
			setViewDate(startOf(value, "month"))
		}
	}, [formattedValue, setViewDate, dateTimeFormat])

	const isValidDate: $PropertyType<
		Required<IDateTimeConfig>,
		"isValidDate"
	> = useCallback(
		(currentDay, selected) => {
			if (configIsValidDate) {
				return configIsValidDate(currentDay, selected)
			}
			if (!min && !max) {
				return alwaysValidDate(currentDay, selected)
			}
			if (min && max) {
				return isBetween(currentDay, min, max, "day", "[]")
			}
			if (min) {
				return isSameOrAfter(currentDay, min, "day")
			}
			if (max) {
				return isSameOrBefore(currentDay, max, "day")
			}
			return true
		},
		[configIsValidDate, min, max]
	)

	// THE CONTEXT
	const ctx = {
		...props,
		isValidDate,
		...state,
		// Use external hoverDate if provided, otherwise use internal state
		hoverDate:
			externalHoverDate !== undefined ? externalHoverDate : state.hoverDate,
		value,
		onChange: mode === "range" ? handleRangeChange : handleChange,
		dateFormat,
		timeFormat,
		dateTimeFormat,
		setViewDate,
		showView,
		navigateForward,
		navigateBack,
		clearable,
		setHoverDate,
		mode,
		rangeValue,
		onRangeChange,
	}

	return ctx
}

function DateTimeContextProvider(props: IDateTimeConfig) {
	const { children } = props
	const dateTimeBag = useDateTime(props)
	return <DateTimeProvider value={dateTimeBag}>{children}</DateTimeProvider>
}

const uiForView = {
	days: DaysView,
	months: MonthsView,
	years: YearsView,
	time: TimesView,
}

function Container() {
	const { view } = useDateTimeContext()
	const View = uiForView[view]
	return <View />
}

export default function DateTime(props: IDateTimeConfig) {
	return (
		<DateTimeContextProvider {...props}>
			<div className={datetimeClassName}>
				<Container />
			</div>
		</DateTimeContextProvider>
	)
}
